import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/landingData";
import { Loader2, Check, Sparkles } from "lucide-react";

export default function Pricing() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [interval, setInterval] = useState("annual");
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [ent, setEnt] = useState(null);

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data.plans));
    if (user) api.get("/me/entitlements").then(({ data }) => setEnt(data)).catch(() => {});
  }, [user]);

  const startTrial = async (plan) => {
    setLoadingPlan(plan.id);
    try {
      await api.post("/subscriptions/trial", { plan: plan.id });
      await refresh?.();
      toast.success(`Your 7-day ${plan.name} trial has started! Enjoy.`);
      navigate("/app");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setLoadingPlan(null); }
  };

  const checkout = async (plan) => {
    setLoadingPlan(plan.id);
    try {
      const { data } = await api.post("/subscriptions/checkout", { plan: plan.id, interval });
      window.location.href = data.url;
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setLoadingPlan(null); }
  };

  const choose = (plan) => {
    // Pre-launch: logged-out users always go to the landing waitlist.
    if (!user) { navigate("/#start"); return; }
    if (plan.id === "free") { navigate("/app"); return; }
    // Logged-in: offer trial if never used and currently free; otherwise checkout.
    if (ent && !ent.trial_used && ent.plan === "free") startTrial(plan);
    else checkout(plan);
  };

  const ctaLabel = (p) => {
    if (!user) return "Join the waitlist";
    if (p.id === "free") return "Go to app";
    if (ent?.plan === p.id) return "Current plan";
    if (ent && !ent.trial_used && ent.plan === "free") return "Start 7-day free trial";
    return `Get ${p.name}`;
  };

  return (
    <div className="relative z-10 min-h-screen px-5 py-16">
      <div className="pointer-events-none absolute left-1/2 top-20 h-96 w-[700px] max-w-full -translate-x-1/2 glow-violet blur-3xl opacity-30" />
      <div className="mx-auto max-w-6xl">
        <button onClick={() => navigate("/")} className="mb-8 flex justify-center"><img src={LOGO_URL} alt="b→come" className="mx-auto h-12" /></button>
        <h1 className="text-center font-mega text-5xl text-white">Choose Your Plan</h1>
        <p className="mt-3 text-center text-muted-foreground">Every paid plan starts with a <span className="text-gold">7-day free trial</span>. Cancel anytime.</p>
        {!user && (
          <div className="mx-auto mt-6 max-w-2xl rounded-2xl border border-primary/40 bg-primary/[0.05] p-4 text-center">
            <p className="text-sm text-white/85">
              <span className="font-bold text-gold">Pre-launch:</span> b&rarr;come is opening the doors soon.
              Join the waitlist and we&rsquo;ll email you the moment your plan is live.
            </p>
          </div>
        )}

        <div className="mt-8 flex justify-center">
          <div className="inline-flex rounded-full border border-white/10 bg-[#0C0C0E] p-1.5">
            {["monthly", "annual"].map((i) => (
              <button key={i} onClick={() => setInterval(i)} data-testid={`interval-${i}`}
                className={`rounded-full px-6 py-2 text-sm font-semibold capitalize transition-colors ${interval === i ? "bg-gradient-brand text-black" : "text-white/60"}`}>
                {i}{i === "annual" && " · save ~35%"}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-12 grid gap-5 lg:grid-cols-4">
          {plans.map((p) => {
            const price = interval === "annual" ? p.annual : p.monthly;
            const featured = p.popular;
            const isCurrent = ent?.plan === p.id;
            return (
              <div key={p.id} data-testid={`plan-${p.id}`}
                className={`relative flex flex-col rounded-3xl border p-7 ${featured ? "border-primary/50 bg-primary/[0.04]" : "border-white/10 bg-[#0A0A0C]"}`}>
                {featured && <span className="mb-3 w-fit rounded-full bg-gradient-brand px-3 py-1 text-xs font-bold uppercase text-black">Most Popular</span>}
                <h3 className="font-display text-xl font-bold uppercase tracking-tight text-white">{p.name}</h3>
                {p.tagline && <p className="mt-1 text-xs text-white/50">{p.tagline}</p>}
                <p className="mt-3"><span className="font-mega text-4xl text-white">${price}</span><span className="text-sm text-muted-foreground">/{interval === "annual" ? "yr" : "mo"}</span></p>
                {p.id !== "free" && <p className="mt-1 flex items-center gap-1 text-xs font-semibold text-gold"><Sparkles className="h-3.5 w-3.5" /> 7-day free trial</p>}
                <ul className="mt-6 flex-1 space-y-2.5">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-white/80"><Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" />{f}</li>
                  ))}
                </ul>
                <button onClick={() => choose(p)} disabled={loadingPlan === p.id || isCurrent} data-testid={`choose-${p.id}`}
                  className={`mt-7 flex items-center justify-center gap-2 rounded-full py-3 text-sm font-bold uppercase tracking-wide transition-all hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100 ${featured || (p.id !== "free" && !isCurrent) ? "bg-gradient-brand text-black" : "border border-white/12 text-white"}`}>
                  {loadingPlan === p.id ? <Loader2 className="h-5 w-5 animate-spin" /> : ctaLabel(p)}
                </button>
              </div>
            );
          })}
        </div>
        {user && (
          <p className="mt-8 text-center text-xs text-muted-foreground">Test mode — use Stripe test card 4242 4242 4242 4242, any future date &amp; CVC.</p>
        )}
      </div>
    </div>
  );
}
